import { useCallback, useEffect, useRef, useState } from "react";
import {
  downloadRecording,
  listRecordings,
  openSession,
  startRecording,
  stopRecording,
  updateRecordingStatus,
} from "../recording/recordingApi";

const UNAVAILABLE = "Recording is currently unavailable. Please try again.";
const LIVE = ["STARTING", "RECORDING", "STOPPING", "PROCESSING"];

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hh = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

function elapsedSeconds(startedAt) {
  if (!startedAt) return 0;
  return Math.max(0, Math.round((Date.now() - Date.parse(startedAt)) / 1000));
}

export function RecordingPanel({ roomName, displayName, asHost, externalApi, logMeeting }) {
  const [session, setSession] = useState(null);
  const [active, setActive] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(0);
  const [liveOn, setLiveOn] = useState(false);
  const sessionRef = useRef(null);
  const activeIdRef = useRef(null);
  const logRef = useRef(logMeeting);
  sessionRef.current = session;
  activeIdRef.current = active?.id || activeIdRef.current;
  logRef.current = logMeeting;

  const isHost = Boolean(asHost && session?.role === "host");

  const refresh = useCallback(async (nextSession) => {
    const current = nextSession || sessionRef.current;
    if (!current?.meetingId) return;
    const data = await listRecordings(current.meetingId, current.accessToken);
    const rows = data.recordings || [];
    const currentActive = rows.find((row) => LIVE.includes(row.status));
    setActive(currentActive || null);
    if (currentActive?.id) activeIdRef.current = currentActive.id;
    setCompleted(rows.find((row) => row.status === "COMPLETED") || null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    openSession({ roomName, displayName, asHost })
      .then((next) => {
        if (cancelled) return null;
        setSession(next);
        return refresh(next);
      })
      .catch((err) => {
        if (!cancelled && err.status !== 404) setError(err.message || UNAVAILABLE);
      });
    return () => {
      cancelled = true;
    };
  }, [roomName, displayName, asHost, refresh]);

  useEffect(() => {
    if (!session) return undefined;
    const timer = setInterval(() => {
      refresh().catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [session, refresh]);

  useEffect(() => {
    if (!externalApi) return undefined;
    const onStatus = (payload) => {
      const on = Boolean(payload?.on);
      const failed = Boolean(payload?.error);
      setLiveOn(on && !failed);
      const current = sessionRef.current;
      const recordingId = activeIdRef.current;
      if (!current || current.role !== "host" || !recordingId) return;
      if (failed) {
        setError(UNAVAILABLE);
        updateRecordingStatus(recordingId, current.accessToken, "FAILED", UNAVAILABLE)
          .then(() => refresh())
          .catch(() => {});
        logRef.current?.("recording-failed", { meetingId: current.meetingId, recordingId, status: "FAILED" });
        return;
      }
      if (on) {
        setError("");
        updateRecordingStatus(recordingId, current.accessToken, "RECORDING")
          .then(() => refresh())
          .catch(() => {});
        logRef.current?.("recording-started", { meetingId: current.meetingId, recordingId, status: "RECORDING" });
      }
    };
    externalApi.addListener("recordingStatusChanged", onStatus);
    return () => {
      externalApi.removeListener?.("recordingStatusChanged", onStatus);
    };
  }, [externalApi, refresh]);

  const status = active?.status || (liveOn ? "RECORDING" : completed ? "COMPLETED" : "NOT_STARTED");
  const showLive = status === "RECORDING" || status === "STARTING" || liveOn;
  const liveDuration = showLive && active?.startedAt ? formatDuration(elapsedSeconds(active.startedAt)) : "";

  useEffect(() => {
    if (!showLive) return undefined;
    const timer = setInterval(() => setClock((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [showLive]);

  async function onStart() {
    if (!session || !isHost) return;
    setBusy(true);
    setError("");
    try {
      const row = await startRecording(session.meetingId, session.accessToken);
      activeIdRef.current = row.id;
      setActive(row);
      externalApi?.executeCommand("startRecording", { mode: "file" });
      logRef.current?.("recording-requested", {
        meetingId: session.meetingId,
        recordingId: row.id,
        status: "STARTING",
      });
    } catch (err) {
      setError(err.message || UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  }

  async function onStop() {
    if (!session || !isHost) return;
    setBusy(true);
    setError("");
    try {
      const row = await stopRecording(session.meetingId, session.accessToken);
      setActive(row);
      externalApi?.executeCommand("stopRecording", "file");
      logRef.current?.("recording-stopped", {
        meetingId: session.meetingId,
        recordingId: row.id,
        status: "STOPPING",
      });
    } catch (err) {
      setError(err.message || UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  }

  const failedText =
    status === "FAILED" ? active?.errorMessage || UNAVAILABLE : session ? error : "";
  const showHostStart = isHost && !showLive && status !== "PROCESSING" && status !== "STOPPING";
  const showCompleted = Boolean(completed && !showLive && status !== "PROCESSING");
  const showSaving = status === "PROCESSING" || status === "STOPPING";
  if (!showLive && !showSaving && !failedText && !showHostStart && !showCompleted) return null;

  return (
    <div className="pointer-events-auto absolute right-3 top-3 z-50 w-[min(100%-1.5rem,280px)] rounded-xl bg-biz-deep/80 p-3 text-white shadow-soft ring-1 ring-white/10 backdrop-blur-md sm:right-4 sm:top-4">
      {showLive ? (
        <p className="flex items-center gap-2 text-sm font-semibold">
          <span className="inline-block h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" aria-hidden />
          <span>Recording</span>
          {liveDuration ? (
            <span key={clock} className="font-normal text-white/80">
              {liveDuration}
            </span>
          ) : null}
        </p>
      ) : null}

      {status === "PROCESSING" || status === "STOPPING" ? (
        <p className="text-sm text-white/80">Saving recording…</p>
      ) : null}

      {failedText ? <p className="mt-1 text-sm text-red-200">{failedText}</p> : null}

      {showHostStart ? (
        <button type="button" className="biz-btn-primary mt-2 w-full" disabled={busy} onClick={onStart}>
          Start Recording
        </button>
      ) : null}

      {isHost && showLive ? (
        <button
          type="button"
          className="mt-2 w-full rounded-xl bg-white px-3 py-2 text-sm font-semibold text-biz-ink"
          disabled={busy}
          onClick={onStop}
        >
          Stop Recording
        </button>
      ) : null}

      {showCompleted ? (
        <div className="mt-2 text-sm">
          <p className="font-semibold">Recording completed</p>
          <p className="text-white/75">Duration: {formatDuration(completed.duration)}</p>
          {completed.canDownload ? (
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                className="rounded-lg bg-white/10 px-2 py-1 text-xs font-semibold"
                onClick={() => downloadRecording(completed.id, session.accessToken, completed.fileName, true)}
              >
                View Recording
              </button>
              <button
                type="button"
                className="rounded-lg bg-biz-yellow px-2 py-1 text-xs font-semibold text-biz-ink"
                onClick={() => downloadRecording(completed.id, session.accessToken, completed.fileName, false)}
              >
                Download Recording
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
